/**
 * Hook to fetch patient form master data (lookup tables)
 *
 * Fetches all lookup data needed for patient form:
 * - Insurance types
 * - Insurance relationship options
 * - Sex options
 * - Patient cost rates
 * - Elderly insurance certificates
 * - Prefectures
 * - Insurer types (for medical aid)
 */

import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/shared/providers/AuthContext";
import { apiClient } from "@/shared/lib/axios";
import { clinicApiPath } from "@/shared/lib/apiPath";

export interface MasterDataResponse {
  success: boolean;
  message: string;
  data: {
    relationships: Array<{ id: number; name: string }>;
    sexes: Array<{ id: number; name: string }>;
    insurance_types: Array<{ id: number; name: string }>;
    patient_costs: Array<{ id: number; name: string; value?: string }>;
    elderly_insurance_certificates: Array<{
      id: number;
      name: string;
      value?: string;
    }>;
    insurer_types: Array<{
      id: number;
      name: string;
      is_health_insurance: boolean;
    }>;
    prefectures: Array<{ id: number; name: string }>;
  };
}

export const usePatientMasterData = () => {
  const { clinic } = useAuth();
  const siteType = clinic?.site_type || "medix";
  const clinicNumber = clinic?.clinic_number || "";

  return useQuery({
    queryKey: ["patients", siteType, clinicNumber, "master-data"],
    queryFn: async () => {
      const response = await apiClient.get<MasterDataResponse>(
        clinicApiPath(siteType, clinicNumber, '/patients/master-data'),
      );
      return response.data.data;
    },
    enabled: !!siteType && !!clinicNumber,
    staleTime: Infinity, // Master data rarely changes
  });
};

export default usePatientMasterData;
